// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Truck } from 'lucide-react'
import * as api from '../../api/client'
import { useDrugRefValues } from '../../hooks/useDrugRefValues'
import CategoryBadge from '../../components/common/CategoryBadge'
import { formatPhone, formatDob, formatCell } from '../../api/format'
import { friendlyError } from '../../api/errors'
import { PayerStatePill, LoadingState } from '../../components/workflow/StatePills'
import { ShipStatusPill } from '../../components/workflow/ShipStatusPill'
import { Pager, preferredPageSize, rememberPageSize } from '../../components/Pager'
import { ListTable, toServerFilters, type ListTableCap } from '../../components/common/ListTable'
import type { ColumnFilters } from '../../components/common/ColumnFilter'
import PatientComments from '../../components/workflow/PatientComments'
import { useAuth } from '../../auth/AuthContext'
import { SITE_OF_CARE_LABELS } from './ProviderDashboard'
import CustomFieldInput from '../../components/CustomFieldInput'

/**
 * Read-only patient view for an external provider / office nurse. Shows the
 * patient header plus ONLY the prescriptions written by prescribers in the
 * viewer's access set (the server enforces this and field-redacts the patient
 * row). Each Rx opens the read-only scoped Rx detail.
 */
export default function ProviderPatientPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { hasPermission, me } = useAuth()
  const [data, setData] = useState<api.ProviderPatientDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [layout, setLayout] = useState<api.RxViewLayout | null>(null)
  // D-VIEWCOLS-4: the provider patient card has its OWN surface (prov_pt_rx_list),
  // managed in the Patient view builder under "Provider portal — patient card prescriptions".
  const [rxCols, setRxCols] = useState<api.RxViewLayout | null>(null)
  useEffect(() => { api.getRxViewLayout(undefined, 'prov_pt_rx_list').then(setRxCols).catch(() => setRxCols(null)) }, [])
  const drugRefWanted = ((rxCols?.cards as any[] | undefined)?.find(c => c.card_key === 'list_columns')?.fields ?? []).some((f: any) => f.owlrx_table === 'drug_reference')
  // D-PROV-2: the Rx table is SERVER-paged. This list had no bound at all before — not even the
  // 1,000-row cap the other provider lists carried — so a long-tenured specialty patient's full
  // fill history came back in one response, and a Provider Admin sees every prescriber's.
  // `data.rx` is now exactly one page; the page number drives the fetch rather than a slice.
  const [rxPage, setRxPage] = useState(1)
  const [rxPageSize, setRxPageSize] = useState(preferredPageSize(20))
  const rxRows = data?.rx ?? []
  const rxTotal = data?.rx_total ?? data?.rx_count ?? 0

  // Table format, mirroring the pharmacy "Active prescriptions" table. Columns come from the
  // prov_pt_rx_list config; fallback = the default provider set.
  const rxCardCfg = rxCols?.cards?.find(c => c.card_key === 'list_columns')
  const rxDefs = useMemo(() => {
    const cfgFields = (rxCols?.source === 'custom' ? (rxCardCfg?.fields ?? []) : []).filter(f => f.visible)
    const FALLBACK: [string, string][] = [
      ['rx_number', 'Rx #'], ['drug_name', 'Drug'], ['sig', 'Sig'],
      ['quantity_authorized', 'Quantity'], ['ship_status', 'Delivery'], ['last_activity', 'Last activity'],
    ]
    const rawDefs = cfgFields.length > 0
      ? cfgFields.map(f => ({ key: f.owlrx_column, label: f.owlrx_label || f.owlrx_column, custom: f.owlrx_table === 'custom', drugref: f.owlrx_table === 'drug_reference' }))
      : FALLBACK.map(([k, l]) => ({ key: k, label: l, custom: false, drugref: false }))
    // The portal answers "did my patient get their medication?" — so DELIVERY is the status
    // here. The PMS statuses mirrored from PioneerRx (and the clinical state derived from
    // them) describe the pharmacy's internal workflow and are dropped from this surface,
    // whatever the saved column config says. The Delivery column is guaranteed present.
    const PIONEER_COLS = new Set(['state', 'pioneerrx_rx_status', 'pioneerrx_tx_status'])
    const kept = rawDefs.filter(d => !PIONEER_COLS.has(d.key))
    return kept.some(d => d.key === 'ship_status')
      ? kept
      : [...kept, { key: 'ship_status', label: 'Delivery', custom: false, drugref: false }]
  }, [rxCols, rxCardCfg])

  // D-UI-2 — every column of this table has a funnel, answered by the server. The table pages
  // on the server, so a funnel over the rows on screen would present one page as the history.
  // Capabilities come from the provider registry's own report (no second list here). The
  // table keeps its column keys (widths are stored against them); the wire uses the server's.
  const serverKeyOf = useMemo(() => Object.fromEntries(rxDefs.map(d =>
    [d.key, d.custom ? `cf:${d.key}` : d.drugref ? `dr:${d.key}` : d.key])) as Record<string, string>, [rxDefs])
  const [serverCaps, setServerCaps] = useState<Record<string, ListTableCap>>({})
  useEffect(() => {
    let dead = false
    api.getProviderRxColumnCaps()
      .then(r => { if (!dead) setServerCaps(Object.fromEntries((r.items ?? []).map((c: any) => [c.key, c]))) })
      .catch(() => { /* no caps => every funnel reports itself unavailable, never a fake one */ })
    return () => { dead = true }
  }, [])
  const rxCaps = useMemo(() => Object.fromEntries(rxDefs
    .filter(d => serverCaps[serverKeyOf[d.key]])
    .map(d => [d.key, serverCaps[serverKeyOf[d.key]]])) as Record<string, ListTableCap>, [rxDefs, serverCaps, serverKeyOf])
  const [rxFilters, setRxFilters] = useState<ColumnFilters>({})
  const [rxSortKey, setRxSortKey] = useState<string | null>(null)
  const [rxSortDir, setRxSortDir] = useState<'asc' | 'desc'>('desc')
  const rxServerFilters = useMemo(() => toServerFilters(rxFilters, rxCaps).serverFilters
    .map(f => ({ ...f, key: serverKeyOf[f.key] ?? f.key })), [rxFilters, rxCaps, serverKeyOf])
  const rxFilterSig = api.encodeRxFilters(rxServerFilters).join('|')
  // Delivery and Site of care store codes; the cell shows words, so the funnel does too.
  const funnelWords = (key: string, value: string): string | undefined =>
    key === 'site_of_care_type' && SITE_OF_CARE_LABELS[value] ? SITE_OF_CARE_LABELS[value]
      : key === 'ship_status' || key === 'site_of_care_type'
        ? (s => s.charAt(0).toUpperCase() + s.slice(1))(value.replace(/_/g, ' ')) : undefined
  const drugRefVals = useDrugRefValues(rxRows.map((r: any) => r.id), drugRefWanted)
  // Values for PioneerRx/custom mapped rx-table columns (extraction store, keyed list_columns.<col>).
  const [rxCfv, setRxCfv] = useState<Record<string, Record<string, string | null>>>({})
  useEffect(() => {
    const ids = rxRows.map(r => r.id)
    if (ids.length === 0) return
    let dead = false
    api.batchCustomFieldValues('rx', ids).then(r => { if (!dead) setRxCfv(r.values ?? {}) }).catch(() => {})
    return () => { dead = true }
  }, [rxRows])
  const [cfv, setCfv] = useState<api.CustomFieldValues | null>(null)
  const [emv, setEmv] = useState<api.EntityMappedValues | null>(null)
  const [docs, setDocs] = useState<api.PatientDocument[] | null>(null)
  const [dlId, setDlId] = useState<string | null>(null)
  const [canUpload, setCanUpload] = useState(false)
  const [upFile, setUpFile] = useState<File | null>(null)
  const [upType, setUpType] = useState('chart_notes')
  const [upExp, setUpExp] = useState('')
  const [upBusy, setUpBusy] = useState(false)
  const [upErr, setUpErr] = useState<string | null>(null)

  const loadDocs = () => api.getPatientDocuments(id!).then(r => { setDocs(r.documents); setCanUpload(r.can_write) }).catch(() => setDocs([]))

  async function downloadDoc(docId: string) {
    setDlId(docId)
    try {
      const r = await api.downloadPatientDocument(id!, docId)
      window.open(r.url, '_blank', 'noopener')
    } catch { /* surfaced as no-op; doc may not be downloadable */ }
    finally { setDlId(null) }
  }

  async function uploadDoc() {
    if (!upFile) { setUpErr('Choose a file first.'); return }
    setUpBusy(true); setUpErr(null)
    try {
      const form = new FormData()
      form.append('file', upFile)
      form.append('document_type', upType)
      if (upExp) form.append('expiration_date', upExp)
      // prescriber_id + audience are set server-side for provider uploads.
      await api.uploadPatientDocument(id!, form)
      setUpFile(null); setUpExp('')
      await loadDocs()
    } catch (e: any) {
      setUpErr(e?.message || 'Upload failed.')
    } finally { setUpBusy(false) }
  }

  useEffect(() => { api.getRxViewLayout(undefined, 'patient').then(setLayout).catch(() => setLayout(null)) }, [])
  useEffect(() => { if (id) api.getCustomFieldValues('patient', id).then(setCfv).catch(() => setCfv(null)) }, [id])
  useEffect(() => { if (id) api.getEntityMappedValues('patient', id).then(setEmv).catch(() => setEmv(null)) }, [id])
  useEffect(() => { if (id) loadDocs() }, [id])

  useEffect(() => {
    if (!id) return
    let live = true
    setLoading(true); setError(null)
    api.getProviderPatient(id, {
      page: rxPage, pageSize: rxPageSize,
      ...(rxSortKey ? { orderBy: serverKeyOf[rxSortKey] ?? rxSortKey, orderDir: rxSortDir } : {}),
      ...(rxServerFilters.length ? { filters: rxServerFilters } : {}),
    })
      .then(d => { if (live) setData(d) })
      .catch(e => { if (live) setError(friendlyError(e)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }   // a stale page response must not overwrite a newer one
    // rxFilterSig, not the array: a new array identity every render would refetch every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, rxPage, rxPageSize, rxFilterSig, rxSortKey, rxSortDir])

  // A different patient starts at page 1; without this, opening a patient from page 3 of the list
  // would request page 3 of THEIR history and render an empty table.
  useEffect(() => { setRxPage(1); setRxFilters({}) }, [id])

  const documentsSection = (
    <>
      <h2 style={{ fontSize: 16, margin: '18px 0 8px' }}>Documents</h2>
      {canUpload && (
        <div style={{ ...card, marginBottom: 8 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 8 }}>Upload a document</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={upType} onChange={e => setUpType(e.target.value)} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12.5, fontFamily: 'inherit' }}>
              {[['chart_notes', 'Chart notes'], ['lab_results', 'Lab results'], ['nursing_notes', 'Nursing notes'], ['consent_hipaa', 'HIPAA consent'], ['smn', 'Statement of medical necessity'], ['pa', 'Prior authorization'], ['appeal', 'Appeal'], ['benefits_verification', 'Benefits verification']].map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.gif,.doc,.docx" onChange={e => setUpFile(e.target.files?.[0] ?? null)} style={{ fontSize: 12.5 }} />
            <label style={{ fontSize: 12, color: 'var(--color-text-mute)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              expires <input type="date" value={upExp} onChange={e => setUpExp(e.target.value)} style={{ padding: '5px 7px', borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12.5, fontFamily: 'inherit' }} />
            </label>
            <button onClick={uploadDoc} disabled={upBusy || !upFile}
              style={{ background: 'var(--color-primary,#2563eb)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12.5, cursor: upBusy || !upFile ? 'default' : 'pointer', fontFamily: 'inherit', opacity: upBusy || !upFile ? 0.6 : 1 }}>
              {upBusy ? 'Uploading…' : 'Upload'}
            </button>
          </div>
          {upErr && <div style={{ color: '#B23A22', fontSize: 12, marginTop: 6 }}>{upErr}</div>}
        </div>
      )}
      {docs == null ? (
        <div style={{ ...card, color: 'var(--color-text-mute)', fontSize: 13 }}>Loading documents…</div>
      ) : docs.length === 0 ? (
        <div style={{ ...card, color: 'var(--color-text-mute)', fontSize: 13 }}>No documents shared with prescribers for this patient.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {docs.map(d => (
            <div key={d.id} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{d.title || d.document_type || 'Document'}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>
                    {d.document_type}{d.lifecycle_status ? ` · ${d.lifecycle_status}` : ''}{d.received_at ? ` · ${new Date(d.received_at).toLocaleDateString()}` : ''}{d.expiration_date ? ` · expires ${new Date(d.expiration_date).toLocaleDateString()}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {d.has_file && (
                    <button onClick={() => downloadDoc(d.id)} disabled={dlId === d.id}
                      style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--color-primary,#2563eb)' }}>
                      {dlId === d.id ? '…' : 'Download'}
                    </button>
                  )}
                  {d.signature_status && d.signature_status !== 'not_required' && (
                    <span style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>signature: {d.signature_status}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )

  // Only the FIRST load replaces the page. A refetch (a page turn, a funnel, a sort) keeps it
  // mounted: unmounting the table closes the funnel popover on every change.
  if (loading && !data) return <LoadingState label="Loading patient…" />
  // The patient header can 404 when the patient has no in-scope Rx for this prescriber, but the
  // documents endpoint is independently scoped (prescriber_id siloing), so always surface Documents.
  if (error || !data) return (
    <div style={{ padding: '16px', maxWidth: 'var(--page-max, 900px)', margin: '0 auto' }}>
      <button onClick={() => navigate(-1)} style={backBtn}>← Back</button>
      <div style={{ color: 'var(--color-text-mute)', fontSize: 13, margin: '8px 0 14px' }}>
        {error ? 'The full patient record isn’t in your prescriber scope. Documents shared with you appear below.' : ''}
      </div>
      {documentsSection}
    </div>
  )

  const p = data.patient ?? {}
  const name = (p.display_name as string) || '(unnamed)'
  const fields: [string, any][] = [
    ['Date of birth', p.date_of_birth ? formatDob(p.date_of_birth) : null],
    ['Gender', p.gender],
    ['Phone', formatPhone(p.primary_phone ?? p.phone)],
  ]

  // When the pharmacy has saved a Patient layout, providers see the same cards their
  // role(s) are allowed to see (server still field-redacts the patient row). Otherwise
  // the minimal built-in summary is shown.
  const myRoles = new Set(me?.authenticated ? (me.role_ids?.length ? me.role_ids : (me.primary_role_id ? [me.primary_role_id] : [])) : [])
  const isPlatformAdmin = hasPermission('platform.access')
  const fieldValue = (f: any): string => {
    const col = f.owlrx_column
    if (col === 'display_name') return (p.display_name as string) ?? '—'
    if (col === 'gender') return (p.gender as string) ?? (p.sex as string) ?? '—'
    if (col === 'date_of_birth') return formatDob(p.date_of_birth as string)
    if (col === 'primary_phone') return formatPhone((p.primary_phone as string) ?? (p.phone as string))
    if (col === 'height_inches') return p.height_inches != null ? `${p.height_inches}″` : '—'
    if (col === 'weight_ounces') return p.weight_ounces != null ? `${(Number(p.weight_ounces) / 16).toFixed(1)} lb` : '—'
    if (col === 'allergies') return (p.allergies as string) ?? 'None on file'
    if (col === 'address')
      return [p.address_line, p.city, p.state_code, p.postal_code].filter(Boolean).join(', ') || '—'
    const v = (p as any)[col]
    return formatCell(col, v)
  }
  const renderLayout = () => (layout?.cards ?? []).map((c: any) => {
    if (!c.visible || c.kind !== 'fields') return null
    if (!isPlatformAdmin && Array.isArray(c.allowed_roles) && c.allowed_roles.length > 0
        && !c.allowed_roles.some((r: string) => myRoles.has(r))) return null
    const vis = (c.fields ?? []).filter((f: any) => f.visible)
    if (!vis.length) return null
    return (
      <div key={c.card_key} style={{ ...card, marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{c.title}</div>
        {vis.map((f: any) => (
          <div key={`${f.owlrx_table}.${f.owlrx_column}`} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10, padding: '4px 0', fontSize: 13 }}>
            <span style={{ color: 'var(--color-text-mute)' }}>{f.owlrx_label}</span>
            <span>{f.pioneer_path ? (emv?.values?.[`${c.card_key}.${f.owlrx_column}`] || '—') : ((f.owlrx_table === 'custom') ? <CustomFieldInput surface="patient" recordId={id || ''} cardKey={c.card_key} field={f} initialValue={cfv?.values?.[`${c.card_key}.${f.owlrx_column}`]} canWrite={!!cfv?.can_write} canModify={!!cfv?.can_modify} /> : fieldValue(f))}</span>
          </div>
        ))}
      </div>
    )
  })

  return (
    <div style={{ padding: '16px', maxWidth: 'var(--page-max, 900px)', margin: '0 auto' }}>
      <button onClick={() => navigate(-1)} style={backBtn}>← Back</button>

      <h1 style={{ fontSize: 24, margin: '8px 0 2px' }}>{name}</h1>
      <div style={{ color: 'var(--color-text-mute)', fontSize: 13, marginBottom: 14 }}>
        Read-only patient record. You see the prescriptions written by your linked prescriber(s).
      </div>

      {layout?.source === 'custom' ? renderLayout() : (
      <div style={card}>
        {fields.filter(([, v]) => v != null && v !== '').map(([label, v]) => (
          <div key={label} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10, padding: '4px 0', fontSize: 13 }}>
            <span style={{ color: 'var(--color-text-mute)' }}>{label}</span>
            <span>{String(v)}</span>
          </div>
        ))}
      </div>
      )}

      <h2 style={{ fontSize: 16, margin: '18px 0 8px' }}>Prescriptions ({rxTotal})</h2>
      {data.rx.length === 0 && rxServerFilters.length === 0 ? (
        <div style={{ ...card, color: 'var(--color-text-mute)', fontSize: 13 }}>No prescriptions in your access for this patient.</div>
      ) : (
        (() => {
          const cardCfg = rxCardCfg
          const defs = rxDefs
          const subHidden = new Set<string>(((cardCfg?.config as any)?.subtext_hidden as string[] | undefined) ?? [])
          const rel = (iso: string | null) => {
            if (!iso) return '—'
            const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
            if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`
            if (s < 86400) return `${Math.floor(s / 3600)}h ago`
            return `${Math.floor(s / 86400)}d ago`
          }
          // Returns cell CONTENT. ListTable owns the <td> (and with it the width binding), so a
          // renderer that emitted its own would nest a cell inside a cell.
          const cell = (rx: api.ProviderPatientRx, key: string): React.ReactNode => {
            switch (key) {
              case 'rx_number': return (
                <>
                  <div style={{ fontFamily: 'monospace' }}>{rx.rx_number ?? '—'}</div>
                  {!subHidden.has('rx_number') && (rx.refill_number ?? 0) > 0 && <div style={pMuted}>refill {rx.refill_number}</div>}
                </>)
              case 'drug_name': return (
                <>
                  <div style={{ fontWeight: 500 }}>{rx.drug_name ?? '—'}</div>
                  {!subHidden.has('drug_name') && (rx.drug_strength || rx.drug_form) &&
                    <div style={pMuted}>{[rx.drug_strength, rx.drug_form].filter(Boolean).join(' · ')}</div>}
                </>)
              case 'sig': return <div style={pTrunc}>{rx.sig ?? '—'}</div>
              case 'quantity_authorized': return <>{rx.quantity_authorized ?? '—'} {rx.quantity_unit ?? ''}</>
              case 'prescriber_name': return <>{rx.prescriber_name ?? '—'}</>
              case 'ship_status': return (
                <ShipStatusPill size="sm" rxId={rx.id}
                                status={(rx as any).ship_status}
                                deliveredAt={(rx as any).ship_delivered_at} />)
              case 'payer_state': return <>{rx.payer_state ? <PayerStatePill state={rx.payer_state} /> : '—'}</>
              case 'last_activity': return <>{rel(rx.last_state_change_at)}</>
              // Words, as the funnel lists them and as My prescriptions shows them.
              case 'site_of_care_type': return <>{rx.site_of_care_type ? (funnelWords(key, rx.site_of_care_type) ?? rx.site_of_care_type) : '—'}</>
              default: {
                const d = defs.find(x => x.key === key)
                if (d?.drugref) return key === 'category'
                  ? <CategoryBadge name={drugRefVals[rx.id]?.category} color={drugRefVals[rx.id]?.category_color} />
                  : <span style={{ fontSize: 12.5 }}>{String((drugRefVals[rx.id] as any)?.[key] ?? '—')}</span>
                if (d?.custom) return <span style={{ fontSize: 12.5 }}>{rxCfv[rx.id]?.[`list_columns.${key}`] ?? '—'}</span>
                return <>{String((rx as any)[key] ?? '—')}</>
              }
            }
          }
          return (
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              <div style={{ padding: '4px 10px' }}>
                <Pager page={rxPage} totalPages={Math.max(1, Math.ceil(rxTotal / rxPageSize))} total={rxTotal} pageSize={rxPageSize} onPage={setRxPage} onPageSize={n => { rememberPageSize(n); setRxPageSize(n) }} label="prescriptions" />
              </div>
              <ListTable<api.ProviderPatientRx>
                viewKey="prov:patient-rx"
                columns={defs.map(d => ({ key: d.key, label: d.label, render: (rx: api.ProviderPatientRx) => cell(rx, d.key) }))}
                rows={rxRows}
                getRowKey={rx => rx.id}
                loading={loading}
                caps={rxCaps}
                filters={rxFilters}
                onFilters={next => { setRxFilters(next); setRxPage(1) }}
                fetchOptions={(column, term) => api.getProviderRxFacet({
                  column: serverKeyOf[column] ?? column, q: term, patient: id,
                  // Every OTHER active filter narrows the list a column offers.
                  filters: rxServerFilters.filter(f => f.key !== (serverKeyOf[column] ?? column)),
                }).then(r => ({ ...r, options: (r.options ?? []).map(o => ({ ...o, label: funnelWords(column, o.value) ?? o.label })) }))}
                labelFor={funnelWords}
                sortKey={rxSortKey}
                sortDir={rxSortDir}
                onSort={key => {
                  if (!rxCaps[key]?.sortable) return
                  setRxSortDir(d => (rxSortKey === key ? (d === 'asc' ? 'desc' : 'asc') : 'asc'))
                  setRxSortKey(key)
                  setRxPage(1)
                }}
                onRowOpen={rx => navigate(`/rx/${rx.id}`)}
                rowTitle="Double-click to open this prescription"
                emptyText={rxServerFilters.length > 0
                  ? "No prescriptions match these filters. Change or clear a filter in the column header."
                  : "No prescriptions in your access for this patient."}
                skeletonRows={Math.min(rxPageSize, 10)}
                trailingHeader={hasPermission('shipping.create') ? <span aria-label="Quick ship" /> : undefined}
                renderTrailing={hasPermission('shipping.create') ? (rx => (
                  <span onClick={e => e.stopPropagation()}>
                    <button type="button" title="Quick ship" onClick={() => navigate(`/shipments?ship_rx=${rx.id}`)}
                      style={{ border: '1px solid var(--color-border)', background: 'none', borderRadius: 5, padding: '3px 5px', cursor: 'pointer', lineHeight: 0 }}>
                      <Truck size={13} />
                    </button>
                  </span>
                )) : undefined}
              />
              <div style={{ padding: '4px 10px' }}>
                <Pager page={rxPage} totalPages={Math.max(1, Math.ceil(rxTotal / rxPageSize))} total={rxTotal} pageSize={rxPageSize} onPage={setRxPage} showPageSize={false} label="prescriptions" />
              </div>
            </div>
          )
        })()
      )}

      {documentsSection}

      <PatientComments patientId={id!} />
    </div>
  )
}

const backBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: 'var(--color-text-mute)', cursor: 'pointer',
  fontSize: 14, padding: '4px 0', fontFamily: 'inherit',
}
const card: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 10, padding: '12px 14px', background: 'var(--color-surface)',
}
const pMuted: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-mute)' }
const pTrunc: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
